import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'

//UI
import { Checkbox, DummyInput, Input, Select, Textarea } from '@/components/Form'
import { Button, Flex, HStack, Heading, Spacer, Text } from '@chakra-ui/react'

import { toast } from 'react-toastify'

//API
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
//Constant
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { isNullish } from '@/utils/helper'

import EditableTable from '@/components/Table/EditableTable'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { createColumnHelper } from '@tanstack/react-table'
import useVendor from '../../useVendor'

const VendorDetailForm = ({ requestorEmail, vendor, isCreate }) => {
  const methods = useForm()
  const recaptcha = useRef()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { useUpdateVendor, useCreateVendor } = useVendor()

  const columnHelper = createColumnHelper()
  const { useSystemVariableOptions } = useSystemVariable()

  const updateVendor = useUpdateVendor()
  const createVendor = useCreateVendor()

  const contactPersons = useWatch({ name: 'contactPersons', control: methods.control })
  const id = useWatch({ name: '_id', control: methods.control })
  const { remove: removeContactPerson } = useFieldArray({ control: methods.control, name: 'contactPersons' })

  //Translated Data
  const status = vendor?.isTemporary ? 'Open' : isCreate ? 'Draft' : 'Converted'
  const { data: vendorCategoryOptions } = useSystemVariableOptions(SystemVariableType.VENDOR_CATEGORY, {
    status: 'active',
    valueKey: '_id',
  })

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('userId.fullName', {
        size: 500,
        header: () => <>Name</>,
        cell: ({ getValue, row: { original } }) => {
          let userName = getValue()
          let color = 'black'

          if (!getValue() && original?.name && original?.name !== '') {
            userName = original?.name
            color = 'red'
          }
          return <TooltipCell text={userName} color={color} />
        },
      }),
      columnHelper.accessor('userId.email', {
        size: 500,
        header: () => <>Email</>,
        cell: ({ getValue, row: { original } }) => {
          let email = getValue()
          let color = 'black'
          if (!getValue() && original?.email && original?.email !== '') {
            email = original?.email
            color = 'red'
          }
          return <TooltipCell text={email} color={color} />
        },
      }),
      columnHelper.accessor('userId._id', {
        header: () => <>Email</>,
        cell: ({ getValue, row: { index, original } }) => {
          const userId = getValue()
          return (
            <HStack gap={1}>
              {userId && <Button onClick={() => navigate(`/admin/users/${userId}`)}>Detail</Button>}
              <Button colorScheme="red" onClick={() => removeContactPerson(index)}>
                Remove
              </Button>
            </HStack>
          )
        },
      }),
    ],
    [columnHelper, navigate, removeContactPerson]
  )
  useEffect(() => {
    if (!isNullish(vendor)) {
      methods.reset(vendor)
    }
  }, [methods, vendor])

  //Handler

  const onSubmit = async (data) => {
    try {
      dispatch(showLoadingOverlay())

      if (isCreate) {
        const res = await createVendor.mutateAsync({ ...data })
        console.log(res)
        if (res?._id) {
          navigate(`/admin/vendors/${res?._id}`)
        } else {
          navigate(-1)
        }

        // navigate("")
      } else {
        const id = methods.getValues('_id')
        if (!id) {
          throw new Error('Missing Id to update ')
        }
        if (data?.contactPersons) {
          for (const contactPerson of data.contactPersons) {
            if (!contactPerson?.name || !contactPerson?.email) {
              throw new Error('Missing Name / Email for one of contact person')
            }
          }
        }

        await updateVendor.mutateAsync({ id, data })
      }
      methods.reset({})

      // navigate(-1)
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleConvertListed = async () => {
    if (!vendor?._id) {
      toast.error('[Client Error] Missing Vendor ID')
    }
    dispatch(showLoadingOverlay())
    try {
      await updateVendor.mutateAsync({ id: vendor?._id, data: { isTemporary: false } })
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading> Vendor Information </Heading>
        </Flex>

        <Flex direction="column" gap={2} p="5">
          <Input name="name" label={`Company Name`} type="text" placeholder="Enter value" isRow />

          <Input name="address" label={`Registered Address`} placeholder="Enter value" isRow isRequired={isCreate} />

          <Input name="BRNumber" label={`BR Number`} placeholder="00000000" isRow isRequired={isCreate} />

          <Flex direction="column" gap={2}>
            <Input
              name="projectReference"
              label={`Project Reference`}
              placeholder="Enter value"
              isRow
              isRequired={false}
            />

            <Input
              name="supplierNumber"
              label={`Supplier No.`}
              type="text"
              placeholder="Enter value"
              isRow
              isRequired={false}
            />
            <Checkbox name="r4b" label={`R4B`} isRow isRequired={false} />
            <Select
              name="components"
              label={`Components`}
              options={vendorCategoryOptions}
              isRow
              isRequired={false}
              isMulti
            />
            <Textarea name="remarks" label={`Remarks`} placeholder="Enter value" isRow isRequired={false} />

            <DummyInput label="Status" value={status} isRow />

            <Flex gap={2}>
              <Text color={'black.800'} fontWeight={'semibold'}>
                Contact Persons
              </Text>
              <Spacer />

              {!isCreate && (
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={() => navigate(`/admin/users/create?vendorId=${id}&type=vendor`)}
                >
                  Add
                </Button>
              )}
            </Flex>
            <EditableTable columnDef={columnDef} data={contactPersons || []} disabled={true} tableWidth="100%" />

            <Flex gap="3">
              <Button colorScheme="blue" type="submit">
                Save
              </Button>
              {status === 'Open' && (
                <Button colorScheme="green" onClick={handleConvertListed}>
                  Convert to listed
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  )
}

export default VendorDetailForm
